import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  DataTableAddRow,
  DataTableGroupByDropdown,
  Dialog,
  DialogPopup,
  DialogTitle,
  EmptyState,
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  Icon,
  IconTile,
  Text,
  type EntitySelection,
  type BulkAction,
} from "@salestrak/ui";

import { PanelOverlay } from "@salestrak/ui";
import { useYieldToEntityDetail } from "#app/components/entity";
import { ManageDialogHeader } from "#app/components/manage-dialog-header";
import { CustomFieldsTable } from "#app/components/custom-fields/custom-fields-table";
import {
  CustomFieldFormDialog,
  type CustomFieldFormMode,
} from "#app/components/custom-fields/custom-field-form-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
// Type-only: the context imports this dialog, so a value import would cycle.
import type { CustomFieldsDialogIntent } from "#app/lib/custom-fields-dialog-context";
import { CUSTOM_FIELD_LIST } from "#app/lib/filters/list-configs";
import type { CustomFieldRow, CustomFieldType } from "#app/lib/data";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { CreateCustomFieldInput } from "#app/lib/data";
import {
  useFieldTypeSelectorConfig,
  FIELD_TYPE_POOL_ID,
} from "#app/lib/hooks/use-field-type-order";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { useCustomFieldsCount } from "#app/lib/hooks/use-custom-fields-list";
import { useCustomFieldMutations } from "#app/lib/hooks/use-custom-field-mutations";
import { usePermission } from "#app/lib/hooks/use-permission";
import { buildCustomFieldBulkActions } from "#app/components/custom-fields/custom-field-bulk-actions";

const GROUP_BY_LABELS = {
  none: "None",
  type: "Type",
} as const;

// Field-type picker reused by the toolbar action and the empty-state CTA.
// Selecting a type opens the create form for that type.
function CreateFieldSelector({
  onSelect,
  align = "end",
  children,
}: {
  onSelect: (selections: EntitySelection[]) => void;
  align?: "start" | "center" | "end";
  children: ReactElement<Record<string, unknown>>;
}) {
  const fieldTypeSelectorConfig = useFieldTypeSelectorConfig();
  return (
    <EntitySelector
      title="Field type"
      config={fieldTypeSelectorConfig}
      value={[]}
      onValueChange={onSelect}
    >
      <EntitySelectorTrigger render={children} />
      <EntitySelectorContent align={align}>
        <EntitySelectorInput />
        <EntitySelectorPool value={FIELD_TYPE_POOL_ID} />
        <EntitySelectorEmpty>No matching field types</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}

// ---------------------------------------------------------------------------
// Inner content — only mounts when the dialog is open
// ---------------------------------------------------------------------------

function CustomFieldsManageContent({
  intent,
  onClose,
  closeGuardRef,
}: {
  /** Deep-link intent captured at open — seeds the initial form panel. */
  intent: CustomFieldsDialogIntent | null;
  onClose: () => void;
  /** The shell hands this in: the content registers an interceptor for
   *  dialog-level closes (outside click / Escape). Returns true when the
   *  close was intercepted by the unsaved-changes confirm. */
  closeGuardRef: { current: (() => boolean) | null };
}) {
  const [groupByField, setGroupByField] =
    useState<keyof typeof GROUP_BY_LABELS>("type");
  const { searchValue, setSearchValue, sorting, setSorting, where, orderBy } =
    useListQueryState(CUSTOM_FIELD_LIST);
  const { total: totalFields, initialLoading } = useCustomFieldsCount();

  const {
    createCustomField,
    updateCustomField,
    deleteCustomField,
    duplicateCustomField,
    refetchList,
  } = useCustomFieldMutations();

  const handleFieldTypeSelect = useCallback((selections: EntitySelection[]) => {
    const selected = selections[0];
    if (selected) {
      setFormMode({ mode: "create", type: selected.id as CustomFieldType });
    }
  }, []);

  // --- Form dialog state ---
  // The content mounts fresh per open, so an `editField` intent seeds the
  // INITIAL panel only — in-dialog navigation owns it from there.
  const [formMode, setFormMode] = useState<CustomFieldFormMode | null>(() => {
    if (!intent) return null;
    if ("editField" in intent) return { mode: "edit", field: intent.editField };
    // The kind the picker already chose, else Text — the form's opening
    // kind; the switcher in its title changes it either way.
    return { mode: "create", seed: intent.seed, type: intent.type ?? "text" };
  });
  // A `createField` intent came from somewhere that wants the field back
  // (a type's "New Field"): hand it over and close the manager, which the
  // user never asked to see.
  const createIntent = intent && "createField" in intent ? intent : null;
  const handleCreateField = useCallback(
    async (input: CreateCustomFieldInput) => {
      const created = await createCustomField(input);
      if (created && createIntent && "customFieldId" in created) {
        createIntent.onCreated?.(created as CustomFieldDef);
        onClose();
      }
      return created;
    },
    [createCustomField, createIntent, onClose],
  );

  const handleEdit = useCallback((field: CustomFieldDef) => {
    setFormMode({ mode: "edit", field });
  }, []);

  // "+ Create <type> field" as a real row under each type group (the reference
  // per-group create affordance) — opens the create form pre-set to the
  // group's type. Grouped mode only; the "__all__" fallback group has none.
  const renderGroupFooter = useCallback((groupKey: string) => {
    if (groupKey === "__all__") return null;
    const meta = getFieldTypeMeta(groupKey);
    return (
      <DataTableAddRow
        label={`Create ${meta.label.toLowerCase()} field`}
        onClick={() =>
          setFormMode({ mode: "create", type: groupKey as CustomFieldType })
        }
      />
    );
  }, []);

  // --- Unsaved-changes guard: the form panel reports whether it deviates
  // from its initial state; every dismiss path that would silently discard
  // it (panel backdrop press, dialog outside click / Escape, header close)
  // routes through a confirm first. The form's own Cancel / X stay
  // immediate — those are explicit discards. ---
  const formDirtyRef = useRef(false);
  const handleFormDirtyChange = useCallback((dirty: boolean) => {
    formDirtyRef.current = dirty;
  }, []);
  // What the intercepted close targeted: just the panel, or the whole dialog.
  const [pendingDiscard, setPendingDiscard] = useState<
    "form" | "dialog" | null
  >(null);

  const handleCloseForm = useCallback(() => {
    formDirtyRef.current = false;
    setFormMode(null);
  }, []);

  const requestCloseForm = useCallback(() => {
    if (formDirtyRef.current) setPendingDiscard("form");
    else handleCloseForm();
  }, [handleCloseForm]);

  const requestCloseDialog = useCallback(() => {
    if (formDirtyRef.current) setPendingDiscard("dialog");
    else onClose();
  }, [onClose]);

  useEffect(() => {
    closeGuardRef.current = () => {
      if (!formDirtyRef.current) return false;
      setPendingDiscard("dialog");
      return true;
    };
    return () => {
      closeGuardRef.current = null;
    };
  }, [closeGuardRef]);

  const confirmDiscardChanges = useCallback(() => {
    const scope = pendingDiscard;
    setPendingDiscard(null);
    handleCloseForm();
    if (scope === "dialog") onClose();
  }, [pendingDiscard, handleCloseForm, onClose]);

  // --- Delete dialog ---
  // Registry interlock: reaching this surface already required
  // custom_fields.manage, but DELETING a field destroys its stored values on
  // every item, so it additionally needs an unconditional Delete Items
  // grant. Without it every delete affordance (row menu, bulk bar,
  // form-footer danger zone) disappears.
  const canDelete = usePermission("delete.items");
  const [deleteTarget, setDeleteTarget] = useState<CustomFieldDef | null>(
    null,
  );

  const handleDelete = useCallback((field: CustomFieldDef) => {
    setDeleteTarget(field);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteCustomField(deleteTarget.id);
    await refetchList();
    // handleCloseForm (not bare setFormMode) so the dirty flag resets — the
    // deleted field's open edit form no longer holds recoverable changes.
    handleCloseForm();
    setDeleteTarget(null);
  }, [deleteTarget, deleteCustomField, refetchList, handleCloseForm]);

  // --- Duplicate ---
  const handleDuplicate = useCallback(
    async (field: CustomFieldRow) => {
      await duplicateCustomField(field.id);
    },
    [duplicateCustomField],
  );

  // --- Bulk actions ---
  const bulkActions = useMemo<BulkAction<CustomFieldRow>[]>(
    () =>
      buildCustomFieldBulkActions({
        canDelete,
        deleteCustomField,
        duplicateCustomField,
        refetchList,
      }),
    [canDelete, deleteCustomField, duplicateCustomField, refetchList],
  );

  // No fields at all → styled empty state, no table/toolbar/search/group
  // (nothing for those controls to act against).
  const empty: ReactNode = (
    <Box fill="flex" align="center" justify="center" padding="xl">
      <EmptyState
        media="icon"
        icon="swatch-book"
        title="Create your first custom field"
        description="Custom fields let you track the information that matters most. Add them to any item type."
        actions={
          <CreateFieldSelector onSelect={handleFieldTypeSelect} align="center">
            <Button>
              <Icon name="plus" />
              <Text>Create field</Text>
            </Button>
          </CreateFieldSelector>
        }
      />
    </Box>
  );

  return (
    // PanelOverlay anchors to this container, and a plain Box is
    // `position: static`, so the relative position is explicit.
    <Box direction="column" fill="flex" overflow="hidden" position="relative">
      <ManageDialogHeader onClose={requestCloseDialog}>
        <Box align="center" direction="row" gap="sm">
          {/* The manage-dialog identity tile. Neutral ink on purpose — an
              identity mark, not a themed accent (`primary` would re-seat to
              the workspace accent). */}
          <IconTile bordered name="swatch-book" size="sm" />
          <Text size="sm" weight="semibold">
            Custom Field Manager
          </Text>
        </Box>
      </ManageDialogHeader>

      <Box direction="column" fill="flex" overflow="hidden">
        {totalFields === 0 && !initialLoading ? (
          empty
        ) : (
          <CustomFieldsTable
            where={where ?? {}}
            orderBy={orderBy}
            groupByField={groupByField}
            sorting={sorting}
            onSortingChange={setSorting}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={canDelete ? handleDelete : undefined}
            bulkActions={bulkActions}
            groupFooter={renderGroupFooter}
            toolbarLeft={
              <DataTableGroupByDropdown
                groupByField={groupByField}
                setGroupByField={(value) =>
                  setGroupByField(value as keyof typeof GROUP_BY_LABELS)
                }
                groupByLabels={GROUP_BY_LABELS}
              />
            }
            toolbarActions={
              // Same 24px `xs` rhythm as the rest of the toolbar row; plain
              // label, no glyph — the picker it opens carries the type icons.
              <CreateFieldSelector onSelect={handleFieldTypeSelect}>
                <Button size="xs">
                  <Text size={null}>Create new</Text>
                </Button>
              </CreateFieldSelector>
            }
          />
        )}
      </Box>

      {/* Create / Edit sidebar panel — absolute overlay on right edge, full
          dialog height. Background fill matching the dialog body — the
          scrim (a plain dim, no blur) is what separates the layers. */}
      {formMode ? (
        <PanelOverlay onDismiss={requestCloseForm}>
          <CustomFieldFormDialog
            formMode={formMode}
            onClose={handleCloseForm}
            onCreate={handleCreateField}
            onUpdate={updateCustomField}
            onDelete={canDelete ? handleDelete : undefined}
            onDirtyChange={handleFormDirtyChange}
          />
        </PanelOverlay>
      ) : null}

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the {deleteTarget?.name} field? It
              will no longer be available to use in any issues or forms. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button color="destructive" onClick={confirmDelete}>
              Delete field
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>

      {/* Unsaved-changes confirmation — intercepted dismisses land here. */}
      <AlertDialog
        open={!!pendingDiscard}
        onOpenChange={(open) => {
          if (!open) setPendingDiscard(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              This field has changes that haven&rsquo;t been saved. Closing now
              will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Keep editing
            </AlertDialogClose>
            <Button color="destructive" onClick={confirmDiscardChanges}>
              Discard changes
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dialog shell — open/close controlled by props from the provider
// ---------------------------------------------------------------------------

export function CustomFieldsManageDialog({
  open,
  intent = null,
  onClose,
}: {
  open: boolean;
  /** Deep-link intent captured at open — `editField` lands with that field's
   *  edit panel already up. The content remounts per open, so it seeds the
   *  INITIAL panel state only. */
  intent?: CustomFieldsDialogIntent | null;
  onClose: () => void;
}) {
  // Content registers an interceptor here: dialog-level closes (outside
  // click, Escape) defer to the unsaved-changes confirm when the form panel
  // is dirty.
  const closeGuardRef = useRef<(() => boolean) | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) return;
    if (closeGuardRef.current?.()) return;
    onClose();
  };

  // A Created By chip press opens the profile island, which renders in the
  // shell beneath this modal — the dialog yields, through the same guarded
  // close an outside click takes (a dirty form still gets its confirm).
  useYieldToEntityDetail(open, () => handleOpenChange(false));

  // One Dialog at every width — a bounded window on desktop, a full-height
  // sheet on a phone.
  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      // The phone sheet: a work surface takes the full height.
      sheetSize="full"
    >
      <DialogPopup
        // The manage-dialog room rests on the page fill: the create/edit
        // panel (popover fill) reads as the raised layer and its tables read
        // on the surface they read on everywhere.
        surface="background"
        size="window"
        // Don't move initial focus onto the toolbar's first control — Chrome
        // paints a :focus-visible ring for that programmatic focus, leaving a
        // box around the group-by chip on open. Focus stays trapped in the
        // modal; keyboard users get the ring once they Tab to it themselves.
        initialFocus={false}
        showCloseButton={false}
      >
        {/* The content renders its own header (icon + title + close); this
            satisfies the accessible-name requirement on the dialog popup. */}
        <DialogTitle visuallyHidden>Custom Field Manager</DialogTitle>
        <CustomFieldsManageContent
          intent={intent}
          onClose={onClose}
          closeGuardRef={closeGuardRef}
        />
      </DialogPopup>
    </Dialog>
  );
}
